import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { CookieService } from "ngx-cookie-service";
import { EMPTY, of } from "rxjs";
import {
  catchError,
  exhaustMap,
  map,
  mergeMap,
  switchMap,
} from "rxjs/operators";
import { WelcomeScreenModalComponent } from "src/app/shared/components/modals/welcome-screen-modal/welcome-screen-modal.component";
import { ConfigStorageService } from "src/app/shared/services/config-storage/config-storage.service";
import { HelpWidgetService } from "src/app/shared/services/help-widget/help-widget.service";
import { AuthToken } from "../../shared/interfaces/auth-token";
import { AuthService } from "../../shared/services/auth/auth.service";
import * as fromAccount from "../actions/account.actions";
import * as fromAuth from "../actions/auth.actions";
import * as fromUI from "../actions/ui.actions";
import { JiraWidgetService } from "src/app/shared/services/jira-widget/jira-widget.service";
import { AuthKeyclockUrl } from "src/app/shared/interfaces/auth-keyclock";
import { AccountService } from "src/app/shared/services/account/account.service";
import { MatDialog } from "@angular/material/dialog";
import { NotificationsService } from "src/app/shared/services/notifications/notifications.service";
import { createNotification } from "src/app/shared/utils/create-notification";
import { TranslateService } from "@ngx-translate/core";

@Injectable()
export class AuthEffect {
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.LOGIN),
      mergeMap((action: fromAuth.Login) =>
        this.authService.login(action.payload).pipe(
          map((authToken: AuthToken) => {
            this.authService.saveToStorage(authToken);
            return {
              type: fromAuth.LOGIN_COMPLETED,
              payload: {
                authToken: authToken,
              },
            };
          }),
          catchError((httpError) => {
            return of({
              type: fromAuth.LOGIN_FAILED,
              payload: httpError.error,
            });
          }),
        ),
      ),
    ),
  );

  readToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.READ_TOKEN),
      mergeMap(() =>
        this.authService.getFromStorage().pipe(
          map((authToken: AuthToken) => {
            if (authToken) {
              return {
                type: fromAuth.LOGIN_COMPLETED,
                payload: { authToken: authToken },
              };
            } else {
              return {
                type: fromAuth.READ_TOKEN_FAILED,
              };
            }
          }),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  loginCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.LOGIN_COMPLETED),
      mergeMap((action: fromAuth.LoginCompleted) => {
        this.configStorage.load().subscribe((config) => {
          this.jiraWidget.showJiraWidget();

          if (
            !this.cookieService.check("cyber-skiller-skip-welcome") &&
            config.showWelcomeScreen
          ) {
            this.modal.open(WelcomeScreenModalComponent, {
              data: { modalTitle: "GLOBAL.WELCOME_SCREEN.TITLE" },
            });
          }
        });
        return [
          {
            type: fromAccount.GET_MEMBER,
          },
          {
            type: fromUI.INIT_WEBSOCKET,
            payload: action.payload.authToken,
          },
          {
            type: fromUI.GET_RUNNING_EXERCISE,
          },
        ];
      }),
      catchError(() => EMPTY),
    ),
  );

  readTokenFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.READ_TOKEN_FAILED),
      switchMap(() => {
        return of({
          type: fromAuth.LOGOUT,
        });
      }),
      catchError(() => EMPTY),
    ),
  );

  logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromAuth.LOGOUT),
        switchMap(() => {
          this.jiraWidget.hideJiraWidget();
          this.authService.logout();

          return of({});
        }),
      ),
    { dispatch: false },
  );

  loginAd$ = createEffect(() =>
    this.actions$.pipe(
      ofType<fromAuth.LoginKeyclock>(fromAuth.LOGIN_KEYCLOCK),
      mergeMap((action) =>
        this.authService.loginKeyclock(action.payload).pipe(
          map((res: AuthKeyclockUrl) => {
            window.location.href = res.login_url;

            return {
              type: fromAuth.LOGIN_KEYCLOCK_COMPLETED,
              payload: res,
            };
          }),
          catchError((httpError) => {
            return of({
              type: fromAuth.LOGIN_KEYCLOCK_FAILED,
              payload: httpError,
            });
          }),
        ),
      ),
    ),
  );

  terms$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.TERMS),
      exhaustMap((action: fromAuth.Terms) =>
        this.accountService.terms(action.payload).pipe(
          map((res: { logout_url: string }) => {
            if (!action.payload.accepted_terms) {
              this.authService.logout();
              window.location.href = res.logout_url;
            }

            return { type: fromAuth.TERMS_SUCCESS };
          }),
          catchError((httpError: HttpErrorResponse) => {
            if (httpError.status === 400 && !action.payload.accepted_terms) {
              this.authService.logout();
            }

            return of({
              type: fromAuth.TERMS_FAILED,
              payload: httpError.error,
            });
          }),
        ),
      ),
    ),
  );

  termsLeague$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromAuth.TERMS_LEAGUE),
      exhaustMap((action: fromAuth.TermsLeague) =>
        this.accountService.leagueTerms(action.payload).pipe(
          map(() => {
            this.notificationsService.showToastr(
              createNotification({
                content: {
                  message: this.translate.instant(
                    action.payload.accepted_league_terms
                      ? "ACCOUNT.LEAGUE_TERMS.ACCEPTED_SNACKBAR"
                      : "ACCOUNT.LEAGUE_TERMS.REJECTED_SNACKBAR",
                  ),
                },
              }),
            );

            return {
              type: fromAuth.TERMS_LEAGUE_SUCCESS,
              payload: action.payload,
            };
          }),
          catchError((httpError: HttpErrorResponse) => {
            return of({
              type: fromAuth.TERMS_LEAGUE_FAILED,
              payload: httpError.error,
            });
          }),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private modal: MatDialog,
    private cookieService: CookieService,
    private helpWidget: HelpWidgetService,
    private jiraWidget: JiraWidgetService,
    private configStorage: ConfigStorageService,
    private http: HttpClient,
    private accountService: AccountService,
    private notificationsService: NotificationsService,
    private translate: TranslateService,
  ) {}
}
